/**
 * @author: chenlv
 * @date: 2025/01/20
 * @desc: 休闲专区-品类画像页面筛选器
 */
import React, { useMemo } from 'react';
import moment from 'moment';
import { Card } from 'tea-component';
import DbiCommonFilter from '@intelligence/pages/TopCharts/components/CommonFilter';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import useSenTowerTag from '@intelligence/hooks/useSenTowerTag';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { useReleaseTime } from '@intelligence/hooks/useReleaseTime';
import { handleCasualGenreProfileFilterParamsToApi } from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { APPMAGIC_TAG_MAP, APPMAGIC_TAG_TITLE_MAP } from '@intelligence/components/Filter/FilterBase/_config';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import { getProductModelValueByGenreSource } from '../../utils';
import { CASUAL_GENRE_PROFILE_GENRE_SOURCES } from '../../constants';
import { LANGUAGE_CN } from '@home/_config/config';

import type { IGenretorValue } from '@intelligence/v3/utils/genre';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import type { ICommonFilterInstance } from '@intelligence/pages/TopCharts/components/CommonFilter';

interface ICasualGenreProfileFilterProps extends IBaseProps {
  /** 实体类型 */
  entityType: IEntityType;
  /** 筛选器选项数据加载状态 */
  filterLoading?: boolean;
  /** 地区国家列表 */
  regionOptions?: IRegion[] | null;
  /** 日期选择器禁用映射 */
  rangePickerDisabledMap?: IDateRangeDisabledCallbackDateTypeMap;
  initialDateValueMap?: any;
  /** 初始化的品类筛选器value */
  initialGenreValue: IGenretorValue | null;
  /** 筛选器值（受控模式） */
  filterValue?: IRecord;
  /** 筛选器值变更回调 */
  onFilterChange?: (value: IRecord) => void;
  /** 筛选器确认回调 */
  onConfirm?: (value: IRecord, filter: IRecord) => void;
  /** 筛选器重置回调 */
  onReset?: (value?: IRecord) => void | Promise<void>;
  /**
   * 提供给外部调用者在setFormValue前根据自己业务调整formValue
   * @param 本次修改的筛选器的key
   * @param formValue
   * @return 调整后的formValue
   */
  adjustFormValue?: (key: string, formValue: any) => any;
  onChangeExtra?: (key: string, value1: any, value2: any) => void;
  onChangeExtraLast?: (key: string, value1: any, value2: any) => void;
  /** 热门标签选项列表（由 useHotTagsOptions 提供） */
  hotTagsOptions?: any[];
  /** URL filter_tag_name 参数，用于确定 HotTagsSource 默认选中项 */
  filterTagName?: string;
  /** 页面初始化时匹配到的默认 HotTag option，用于 reset 时恢复 */
  hotTagDefaultOption?: any;
  /** 页面初始化时的 reset 基线，包含 Hot Tags 初始化结果 */
  resetBaseFilterValue?: IRecord | null;
  /** HotTagsSource 选项变化时的回调 */
  onHotTagConfirm?: (option: any) => void;
}

const CasualGenreProfileFilter: React.ForwardRefRenderFunction<
ICommonFilterInstance,
ICasualGenreProfileFilterProps
> = (props, ref) => {
  const {
    style,
    className,
    entityType,
    filterLoading,
    regionOptions,
    rangePickerDisabledMap,
    initialDateValueMap,
    initialGenreValue,
    filterValue: externalFilterValue,
    onFilterChange,
    onConfirm,
    onReset,
    adjustFormValue,
    onChangeExtra,
    onChangeExtraLast,
    hotTagsOptions,
    filterTagName,
    hotTagDefaultOption,
    resetBaseFilterValue,
    onHotTagConfirm,
  } = props;

  const { locale, formatMessage } = useIntlExtend();
  const isCn = locale === LANGUAGE_CN;
  const { loadingStd: definitionsLoading, genreMap, filterTags } = useQueryDefinition();
  const { tagLoading, tagConfig } = useSenTowerTag({});
  const { isLoading: tagsLoading } = useFilterTagsOptions();
  const { minReleaseTime, maxReleaseTime, disabledDate, getAllCompaniesFilterParams } = useReleaseTime({});

  // 处理App Magic Tags数据
  const appMagicTagConfig = useMemo(() => {
    if (!filterTags) return {};

    const config: IRecord = {};

    const mapTagData = (tags: any[]) => {
      if (!Array.isArray(tags) || tags.length === 0) {
        return [];
      }

      return tags.map((tag) => {
        const tagValue = tag?.tag || '';
        const tagEN = tag?.tag_en || '';
        const tagCN = tag?.tag_cn || '';
        const tagDefault = tagValue || tagEN || tagCN;

        return {
          value: tagDefault,
          text: (isCn ? tagCN : tagEN) || tagDefault,
          textCN: tagCN || tagDefault,
          textEN: tagEN || tagDefault,
        };
      });
    };

    Object.keys(APPMAGIC_TAG_MAP).forEach((key) => {
      const apiFieldName = APPMAGIC_TAG_MAP[key];
      const tagData = filterTags[apiFieldName];

      config[key] = {
        options: mapTagData(tagData),
        title: APPMAGIC_TAG_TITLE_MAP[key],
      };
    });

    return config;
  }, [isCn, filterTags]);

  const defaultFilterValue = {
    [filterKeyMap.MY_FILTER]: {
      value: '',
    },
    [filterKeyMap.RANGE_TIME]: {
      type: 'month', // 固定月粒度
      value: initialDateValueMap?.month,
    },
    // 品类筛选器使用 GENRETOR_WITH_SEGMENT 作为 key
    [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
      ...initialGenreValue,
      source: initialGenreValue?.source || 'appmagic', // 默认使用App Magic
      mainGenres: initialGenreValue?.mainGenres || [],
      subGenres: initialGenreValue?.subGenres || [],
      level3Genres: initialGenreValue?.level3Genres || [],
      level4Genres: initialGenreValue?.level4Genres || [],
    },
    [filterKeyMap.HOT_TAGS_SOURCE]: {
      value: null,
    },
    [filterKeyMap.REGION]: {
      type: 'market',
      regions: [],
      markets: ['global'],
    },
    [filterKeyMap.PLATFORM]: {
      value: [],
    },
    [filterKeyMap.ALL_COMPANYS]: {
      value: [],
    },
    [filterKeyMap.RELEASE_TIME]: {
      value: ['', ''],
    },
    [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
      value: getProductModelValueByGenreSource('appmagic'),
    },
    // App Magic Tags默认值
    [filterKeyMap.APPMAGIC_TAG_THEMES]: {
      value: [],
    },
    [filterKeyMap.APPMAGIC_TAG_FEATURES]: {
      value: [],
    },
    [filterKeyMap.APPMAGIC_TAG_IP]: {
      value: [],
    },
    [filterKeyMap.APPMAGIC_TAG_SETTINGS]: {
      value: [],
    },
    [filterKeyMap.APPMAGIC_TAG_ART_STYLES]: {
      value: [],
    },
  };

  const resetFilterValue = {
    ...(resetBaseFilterValue || {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month', // 固定月粒度
        value: [moment().subtract(6, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
      },
    }),
    [filterKeyMap.HOT_TAGS_SOURCE]: {
      value: hotTagDefaultOption || null,
    },
  };

  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.RANGE_TIME,
          filterKeyMap.GENRETOR_WITH_SEGMENT,
          filterKeyMap.HOT_TAGS_SOURCE,
          filterKeyMap.REGION,
          filterKeyMap.PLATFORM,
          filterKeyMap.ALL_COMPANYS,
          filterKeyMap.RELEASE_TIME,
        ],
      },
      {
        key: 'game_performance',
        titleKey: 'dbi_filter_group_game_performance',
        filterKeys: [], // 暂无字段，为未来扩展预留
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL],
      },
      {
        key: 'app_magic',
        titleKey: 'dbi_filter_group_app_magic',
        iconCode: 'appmagic',
        filterKeys: [
          filterKeyMap.APPMAGIC_TAG_THEMES,
          filterKeyMap.APPMAGIC_TAG_FEATURES,
          filterKeyMap.APPMAGIC_TAG_IP,
          filterKeyMap.APPMAGIC_TAG_SETTINGS,
          filterKeyMap.APPMAGIC_TAG_ART_STYLES,
        ],
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );

  const filterCfg: TopchartFilterCfgType = {
    ...groupedFilter,
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    hasUrlShortLink: true,
    enableSticky: true,
    defaultValue: defaultFilterValue,
    resetFilterValue,
    companyFilterParams: {
      entity_type: entityType,
      data_sources: ['sensortower', 'appannie'],
      source: 'sensortower',
      metric: 'dau',
      source_metric: { sensortower: ['dau'] },
      region_type: 'region',
      regions: ['global'],
      granularity: 'daily',
      start_date: '',
      end_date: '',
      search_name: '',
      page: 1,
      page_size: 500,
    },
    specialParams: {
      entityType,
      regionOptions,
      myFilterClassName: 'dbi-casual-genre-profile__my-select',
      isStdGenreValuesHandler: true,
      genreOverlayClassName: 'cascade-mode',
      genreOptionsLoading: definitionsLoading,
      genreOptions: genreMap,
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      // Genre筛选器仅支持Sensor Tower、App Magic
      genreSources: CASUAL_GENRE_PROFILE_GENRE_SOURCES,
      disableIegg: true, // 禁用IEGG
      disableAnnie: true, // 禁用App Annie
      disableSensorTower: false, // 启用Sensor Tower
      disableNoChina: false,
      defalutMarket: ['global'],
      rangeTimeType: FilterTimeType.commonRange,
      // 日期筛选器配置：支持日粒度和月粒度
      typeList: ['date', 'month'], // 支持日、月粒度
      myFilterStyle: { width: 140 },
      showSelectType: true, // 显示粒度选择器
      // ReleaseDate筛选器日期范围限制
      // @ts-ignore
      minReleaseTime,
      maxReleaseTime,
      disabledDate,
      getAllCompaniesFilterParams,
      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      // 热门标签筛选器
      hotTagsOptions: hotTagsOptions || [],
      filterTagName,
      hotTagDefaultOption,
      onHotTagConfirm,
      filterTips: {
        [filterKeyMap.RANGE_TIME]: formatMessage({ id: 'dbi_timefilter_info' }),
        [filterKeyMap.GENRETOR_WITH_SEGMENT]: formatMessage({ id: 'dbi_genrefilter_info' }),
      },
    },
    handleFilterToApiParams: (params: any) => handleCasualGenreProfileFilterParamsToApi(params, entityType),
  };

  // 内部状态（非受控模式）
  const [internalFilterValue, setInternalFilterValue] = React.useState(defaultFilterValue);

  // 根据是否传入外部值决定使用受控或非受控模式
  const filterValue = externalFilterValue || internalFilterValue;
  const setFilterVale = (value: IRecord) => {
    if (onFilterChange) {
      onFilterChange(value);
    } else {
      setInternalFilterValue(value as any);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleConfirm = (value: any, filter: any, myFilterValue: any) => {
    setFilterVale(filter);
    if (onConfirm) {
      onConfirm(value, filter);
    }
  };

  const handleReset = (value?: IRecord) => {
    const resetValue = value || resetFilterValue;
    setFilterVale(resetValue);
    if (onReset) {
      void onReset(resetValue);
    }
  };

  return (
    <div style={style} className={className}>
      <Card>
        <Card.Body>
          <DbiCommonFilter
            ref={ref}
            // @ts-ignore
            filterLoading={filterLoading || tagLoading || tagsLoading}
            filterCfg={filterCfg}
            rangeTimeComponentDefault={initialDateValueMap}
            rangePickerDisabledMap={rangePickerDisabledMap}
            resetBaseValue={resetFilterValue}
            genreComponentDefault={initialGenreValue}
            defaultValue={defaultFilterValue}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            filterConfirm={handleConfirm}
            filterReset={handleReset}
            adjustFormValue={adjustFormValue}
            onChangeExtra={onChangeExtra}
            onChangeExtraLast={onChangeExtraLast}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(CasualGenreProfileFilter);
